import { createAuthClient } from 'better-auth/react';
import {
  organizationClient,
  twoFactorClient,
  magicLinkClient,
  adminClient,
  anonymousClient,
} from 'better-auth/client/plugins';
import { oauthProviderClient } from '@better-auth/oauth-provider/client';
import type { QueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import { env } from '@logspot-platform/env/admin';
import { ac, roles } from '@/lib/permissions';
import { activeOrgIdAtom } from '@/state/atoms';
const API_URL: string = env.VITE_APP_API_URL || '';

// Resolve to absolute URL — better-auth client requires a full URL for baseURL,
// but in dev we use a relative path (/api) that goes through the Vite proxy.
export const authBaseURL = API_URL.startsWith('/')
  ? `${window.location.origin}${API_URL}/auth`
  : `${API_URL}/auth`;

/**
 * Better-auth client for the frontend.
 *
 * This client handles authentication with session cookies (not JWT tokens).
 * It communicates with the better-auth handler mounted at /api/auth/*.
 */
export const authClient = createAuthClient({
  baseURL: authBaseURL,
  // Better Auth's session-refresh manager defaults to refetching /get-session
  // on every window-focus event (rate-limited to 5s). For an SPA with a single
  // tab and TanStack Query already caching the session via sessionOptions(),
  // that's pure noise — verified at better-auth/dist/client/session-refresh.mjs:14.
  // Cross-tab signout still propagates via the broadcast channel.
  sessionOptions: {
    refetchOnWindowFocus: false,
  },
  fetchOptions: {
    onError: async (context) => {
      if (context.response.status === 429) {
        const retryAfter = context.response.headers.get('X-Retry-After');
        console.warn(`Rate limited by auth server. Retry after ${retryAfter}s`);
      }
    },
  },
  plugins: [
    organizationClient({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- better-auth's AC type generics require a cast here
      ac: ac as never,
      roles,
    }),
    twoFactorClient(),
    adminClient(),
    magicLinkClient(),
    // Surfaces `session.user.isAnonymous` for one-click demo visitors (the
    // DemoBanner reads it to label the synthetic demo org).
    anonymousClient(),
    // Surfaces the server `oauthProvider` endpoints on the client (notably
    // `authClient.oauth2.consent`) so the /consent page can approve or deny an
    // MCP client's OAuth request. See apps/admin/src/routes/consent.tsx.
    oauthProviderClient(),
  ],
});

// Export commonly used methods
export const { signIn, signUp, signOut, getSession } = authClient;

// Switch the active organization and bring all client caches into a
// consistent state with the new org cookie:
//   1. server-side: better-auth /organization/set-active updates the cookie
//   2. better-auth atom: the org client plugin's atomListener auto-refreshes
//      $sessionSignal on /organization/set-active, so useSession() consumers
//      see the new activeOrganizationId. The signal triggers a /get-session
//      fetch internally (Better Auth re-pulls the session into the atom).
//   3. TanStack Query session cache: remove ['auth', 'session'] so the next
//      ensureQueryData(sessionOptions()) re-fetches (it ignores invalidation).
//   4. TanStack Query org-scoped caches: cancelQueries aborts any in-flight
//      requests (prevents stale-projectId 404s on the wire), then removeQueries
//      wipes projects, account, queries, events, etc. without triggering
//      immediate refetches on active observers — the next React render drives
//      new fetches with correct keys.
//
// Excluded from the reset: ['auth', 'session'] (removed above) and
// ['auth', 'organizations', ...] (the user's org membership list, which
// is not org-scoped; invalidated since a create or invite join changes it).
export const setActiveOrganization = async (
  organizationId: string,
  queryClient?: QueryClient,
) => {
  const store = getDefaultStore();
  const result = await authClient.organization.setActive({ organizationId });
  if (!result.error && queryClient) {
    // Point the org-scoped state at the new active org. The derived
    // projectIdAtom will now read from the new org's slot in the persisted
    // projectsByOrgAtom map automatically.
    store.set(activeOrgIdAtom, organizationId);
    queryClient.removeQueries({ queryKey: ['auth', 'session'] });
    void queryClient.invalidateQueries({ queryKey: ['auth', 'organizations'] });
    const predicate = (query: { queryKey: unknown }) => {
      const key = query.queryKey;
      if (Array.isArray(key) && key[0] === 'auth') {
        if (key[1] === 'session') return false;
        if (key[1] === 'organizations') return false;
      }
      return true;
    };
    // Cancel in-flight requests first — a fetch already on the wire for the
    // old projectId would otherwise complete after the cookie flips and hit
    // the backend with a mismatched org → 404.
    await queryClient.cancelQueries({ predicate });
    // removeQueries (not resetQueries) wipes cache entries without triggering
    // refetches on active observers. The next React render drives new fetches;
    // by then useProjects suspends on the empty ['projects'] cache, preventing
    // any stale projectId from reaching the backend.
    queryClient.removeQueries({ predicate });
  }
  return result;
};

// Export useSession with stable fetching behavior (no refetch on focus/click)
export const useSession = () => {
  return authClient.useSession();
};

// Organization role constants — used by member management UIs (invite dialog, role selector)
export const ORG_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  LIMITED: 'limited',
} as const;

export type OrgRole = (typeof ORG_ROLES)[keyof typeof ORG_ROLES];
